import {
  Children,
  cloneElement,
  isValidElement,
  type JSX,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../../../lib/cn.js';

// Numbered steps, matching the install-tutorial look: a [44px_1fr] grid with a
// mono lime number and hairline separators. Authored as:
//   <Steps>
//     <Step title="Install">…</Step>
//     <Step title="Authenticate">…</Step>
//   </Steps>
// <Steps> injects the auto-incrementing number + the "last" flag.

interface StepInternalProps {
  _n?: number;
  _last?: boolean;
}

export interface StepProps extends StepInternalProps {
  title: string;
  children?: ReactNode;
}

export function Steps({ children }: { children?: ReactNode }): JSX.Element {
  const items = Children.toArray(children).filter(isValidElement) as ReactElement<StepProps>[];
  return (
    <div className="my-5 border-t border-white/[0.06]">
      {items.map((child, i) =>
        cloneElement(child, { key: i, _n: i + 1, _last: i === items.length - 1 }),
      )}
    </div>
  );
}

export function Step({ title, children, _n = 1, _last = false }: StepProps): JSX.Element {
  const num = String(_n).padStart(2, '0');
  return (
    <div
      className={cn(
        'grid grid-cols-[44px_1fr] gap-5 border-b border-white/[0.06] py-6',
        _last && 'border-b-0',
      )}
    >
      <span className="pt-0.5 font-mono text-[13px] tracking-[0.04em] text-accent-lime">{num}</span>
      <div>
        <h3 className="m-0 mb-2 font-sans text-[16px] font-medium leading-[1.3] tracking-[-0.01em] text-primary">
          {title}
        </h3>
        <div className="text-[14px] leading-[1.6] text-secondary [&>p:not(:first-child)]:mt-2.5 [&>p]:m-0">
          {children}
        </div>
      </div>
    </div>
  );
}
